/**
 * Secure Storage Wrapper - Phase 3 Security Migration
 *
 * ARCHITECTURE: sessionStorage-based security layer
 * PURPOSE: Migrate from persistent localStorage to session-scoped sessionStorage
 *
 * SECURITY IMPROVEMENTS:
 * - sessionStorage: Auto-cleared on tab close (reduces XSS attack surface)
 * - Same-origin policy: More restrictive than localStorage
 * - Session-scoped: No persistence across browser restarts
 * - Type-safe: TypeScript interfaces for all operations
 *
 * MIGRATION FROM localStorage:
 * - Tokens: Previously persistent → Now session-only
 * - User data: Previously persistent → Now session-only
 * - Permissions: Previously persistent → Now session-only
 *
 * TRADE-OFFS:
 * ✅ More secure: Tokens don't persist across browser sessions
 * ✅ XSS protection: Reduced attack window (tab lifetime only)
 * ❌ User experience: Must re-login after browser restart
 * ❌ Cross-tab sync: sessionStorage doesn't fire 'storage' events
 *
 * IMPLEMENTATION NOTES:
 * - All auth-related data migrated to sessionStorage
 * - Cross-tab sync still works within same browser session
 * - Storage event listeners remain for cross-tab coordination
 * - Fallback to localStorage for non-sensitive preference data
 */

import { browser } from '$app/environment';
import { createLogger } from '$lib/utils/logger';

const logger = createLogger('utils.secure-storage');

/**
 * Type-safe storage keys for authentication data
 */
export const AUTH_STORAGE_KEYS = {
	TOKEN: 'token',
	REFRESH_TOKEN: 'refresh_token',
	TOKEN_TYPE: 'token_type',
	TOKEN_EXPIRES_AT: 'token_expires_at',
	TOKEN_TIMESTAMP: 'token_timestamp',
	USER_DATA: 'user_data',
	PERMISSIONS: 'permissions',
	AUTH_VERIFIED: 'auth_verified',
	AUTH_VERIFIED_TIMESTAMP: 'auth_verified_timestamp'
} as const;

/**
 * Secure storage wrapper using sessionStorage for auth-sensitive data
 *
 * SECURITY: sessionStorage is cleared on tab close, reducing XSS attack surface
 * SCOPE: Session-only, no persistence across browser restarts
 */
export const secureStorage = {
	/**
	 * Get authentication token
	 * @returns Token string or null if not found
	 */
	getToken(): string | null {
		if (!browser) return null;
		const token = sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
		return token;
	},

	/**
	 * Set authentication token
	 * @param token - JWT access token
	 */
	setToken(token: string): void {
		if (!browser) return;
		sessionStorage.setItem(AUTH_STORAGE_KEYS.TOKEN, token);
		logger.debug('Token stored in sessionStorage');
	},

	/**
	 * Clear authentication token
	 */
	clearToken(): void {
		if (!browser) return;
		sessionStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
		logger.debug('Token cleared from sessionStorage');
	},

	/**
	 * Get refresh token
	 * @returns Refresh token string or null
	 */
	getRefreshToken(): string | null {
		if (!browser) return null;
		return sessionStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
	},

	/**
	 * Set refresh token
	 * @param token - JWT refresh token
	 */
	setRefreshToken(token: string): void {
		if (!browser) return;
		sessionStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, token);
		logger.debug('Refresh token stored in sessionStorage');
	},

	/**
	 * Clear refresh token
	 */
	clearRefreshToken(): void {
		if (!browser) return;
		sessionStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
		logger.debug('Refresh token cleared from sessionStorage');
	},

	/**
	 * Get token type (default: Bearer)
	 * @returns Token type string
	 */
	getTokenType(): string {
		if (!browser) return 'Bearer';
		return sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN_TYPE) || 'Bearer';
	},

	/**
	 * Set token type
	 * @param type - Token type (e.g., 'Bearer')
	 */
	setTokenType(type: string): void {
		if (!browser) return;
		sessionStorage.setItem(AUTH_STORAGE_KEYS.TOKEN_TYPE, type);
	},

	/**
	 * Get token expiration timestamp
	 * @returns Token expiry as Date or null
	 */
	getTokenExpiry(): Date | null {
		if (!browser) return null;
		const expiryStr = sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT);
		if (!expiryStr) return null;

		try {
			const expiryTime = parseInt(expiryStr, 10);
			if (isNaN(expiryTime)) return null;
			return new Date(expiryTime);
		} catch (e) {
			logger.error('Failed to parse token expiry', { error: e });
			return null;
		}
	},

	/**
	 * Set token expiration timestamp
	 * @param expiry - Expiration date
	 */
	setTokenExpiry(expiry: Date): void {
		if (!browser) return;
		sessionStorage.setItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT, expiry.getTime().toString());
		logger.debug('Token expiry stored', { expiresAt: expiry.toISOString() });
	},

	/**
	 * Clear token expiration
	 */
	clearTokenExpiry(): void {
		if (!browser) return;
		sessionStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN_EXPIRES_AT);
	},

	/**
	 * Update token timestamp (for tracking token refresh)
	 */
	updateTokenTimestamp(): void {
		if (!browser) return;
		const now = Date.now();
		sessionStorage.setItem(AUTH_STORAGE_KEYS.TOKEN_TIMESTAMP, now.toString());
		logger.debug('Token timestamp updated', { timestamp: new Date(now).toISOString() });
	},

	/**
	 * Get token timestamp
	 * @returns Timestamp as number or null
	 */
	getTokenTimestamp(): number | null {
		if (!browser) return null;
		const timestamp = sessionStorage.getItem(AUTH_STORAGE_KEYS.TOKEN_TIMESTAMP);
		if (!timestamp) return null;
		const parsed = parseInt(timestamp, 10);
		return isNaN(parsed) ? null : parsed;
	},

	/**
	 * Get user data
	 * @returns Parsed user data or null
	 */
	getUserData<T = any>(): T | null {
		if (!browser) return null;
		const userData = sessionStorage.getItem(AUTH_STORAGE_KEYS.USER_DATA);
		if (!userData) return null;

		try {
			return JSON.parse(userData) as T;
		} catch (e) {
			logger.error('Failed to parse user data', { error: e });
			return null;
		}
	},

	/**
	 * Set user data
	 * @param data - User profile data
	 */
	setUserData(data: any): void {
		if (!browser) return;
		sessionStorage.setItem(AUTH_STORAGE_KEYS.USER_DATA, JSON.stringify(data));
		logger.debug('User data stored in sessionStorage', { userId: data?.id });
	},

	/**
	 * Clear user data
	 */
	clearUserData(): void {
		if (!browser) return;
		sessionStorage.removeItem(AUTH_STORAGE_KEYS.USER_DATA);
		logger.debug('User data cleared from sessionStorage');
	},

	/**
	 * Get permissions data
	 * @returns Parsed permissions or null
	 */
	getPermissions<T = any>(): T | null {
		if (!browser) return null;
		const permissions = sessionStorage.getItem(AUTH_STORAGE_KEYS.PERMISSIONS);
		if (!permissions) return null;

		try {
			return JSON.parse(permissions) as T;
		} catch (e) {
			logger.error('Failed to parse permissions', { error: e });
			return null;
		}
	},

	/**
	 * Set permissions data
	 * @param permissions - User permissions
	 */
	setPermissions(permissions: any): void {
		if (!browser) return;
		sessionStorage.setItem(AUTH_STORAGE_KEYS.PERMISSIONS, JSON.stringify(permissions));
		logger.debug('Permissions stored in sessionStorage', {
			permissionCount: permissions?.direct_permissions?.length || 0
		});
	},

	/**
	 * Clear permissions data
	 */
	clearPermissions(): void {
		if (!browser) return;
		sessionStorage.removeItem(AUTH_STORAGE_KEYS.PERMISSIONS);
		logger.debug('Permissions cleared from sessionStorage');
	},

	/**
	 * Check if authentication is verified
	 * @returns True if auth is verified
	 */
	isAuthVerified(): boolean {
		if (!browser) return false;
		return sessionStorage.getItem(AUTH_STORAGE_KEYS.AUTH_VERIFIED) === 'true';
	},

	/**
	 * Mark authentication as verified
	 */
	setAuthVerified(verified: boolean): void {
		if (!browser) return;
		if (verified) {
			sessionStorage.setItem(AUTH_STORAGE_KEYS.AUTH_VERIFIED, 'true');
			sessionStorage.setItem(
				AUTH_STORAGE_KEYS.AUTH_VERIFIED_TIMESTAMP,
				Date.now().toString()
			);
			logger.debug('Authentication marked as verified');
		} else {
			sessionStorage.removeItem(AUTH_STORAGE_KEYS.AUTH_VERIFIED);
			sessionStorage.removeItem(AUTH_STORAGE_KEYS.AUTH_VERIFIED_TIMESTAMP);
		}
	},

	/**
	 * Set API key authentication flag
	 */
	setApiKeyAuth(value: string): void {
		if (!browser) return;
		sessionStorage.setItem('using_api_key_auth', value);
		logger.debug('API key auth flag set');
	},

	/**
	 * Get API key authentication flag
	 */
	getApiKeyAuth(): string | null {
		if (!browser) return null;
		return sessionStorage.getItem('using_api_key_auth');
	},

	/**
	 * Clear API key authentication flag
	 */
	clearApiKeyAuth(): void {
		if (!browser) return;
		sessionStorage.removeItem('using_api_key_auth');
	},

	/**
	 * Clear ALL authentication data (full logout)
	 *
	 * SECURITY: Comprehensive cleanup to prevent data leakage
	 */
	clearAll(): void {
		if (!browser) return;

		// Clear all auth-related sessionStorage items
		Object.values(AUTH_STORAGE_KEYS).forEach((key) => {
			sessionStorage.removeItem(key);
		});

		// Also clear API key auth flag
		this.clearApiKeyAuth();

		logger.info('All authentication data cleared from sessionStorage');
	},

	/**
	 * Check if session has valid token
	 * @returns True if token exists and not expired
	 */
	hasValidSession(): boolean {
		if (!browser) return false;

		const token = this.getToken();
		const expiry = this.getTokenExpiry();

		if (!token || !expiry) {
			return false;
		}

		const isExpired = expiry.getTime() <= Date.now();
		logger.debug('Session validity check', {
			hasToken: !!token,
			isExpired,
			expiresAt: expiry.toISOString()
		});

		return !isExpired;
	}
};

/**
 * Migration helper: Copy data from localStorage to sessionStorage
 *
 * USE CASE: One-time migration for existing users on first load after deployment
 * CLEANUP: Remove after migration period (estimate: 1-2 weeks)
 *
 * @returns True if migration was performed
 */
export function migrateFromLocalStorage(): boolean {
	if (!browser) return false;

	let migrated = false;

	// Check if already migrated
	const migrationFlag = sessionStorage.getItem('migrated_from_localStorage');
	if (migrationFlag === 'true') {
		return false;
	}

	// Migrate each key
	Object.values(AUTH_STORAGE_KEYS).forEach((key) => {
		const value = localStorage.getItem(key);
		if (value) {
			sessionStorage.setItem(key, value);
			localStorage.removeItem(key); // Clean up old storage
			migrated = true;
		}
	});

	if (migrated) {
		sessionStorage.setItem('migrated_from_localStorage', 'true');
		logger.info('Successfully migrated auth data from localStorage to sessionStorage');
	}

	return migrated;
}

/**
 * Development/debugging helper: Log current storage state
 *
 * WARNING: Contains sensitive data - use only in development
 */
export function debugStorageState(): void {
	if (!browser || import.meta.env.PROD) return;

	const state = {
		hasToken: !!secureStorage.getToken(),
		hasRefreshToken: !!secureStorage.getRefreshToken(),
		tokenType: secureStorage.getTokenType(),
		tokenExpiry: secureStorage.getTokenExpiry()?.toISOString(),
		hasUserData: !!secureStorage.getUserData(),
		hasPermissions: !!secureStorage.getPermissions(),
		isAuthVerified: secureStorage.isAuthVerified(),
		hasValidSession: secureStorage.hasValidSession()
	};

	logger.debug('Storage state', state);
}
